import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import { ErrorLike } from '@apollo/client';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  WidgetTemplate,
} from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import DashboardContentWithRightSidebar from '@lib/layouts/DashboardLayout/DashboardContentWithRightSidebar/DashboardContentWithRightSidebar';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { GetUrlParams } from '@lib/enums/urls';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import WidgetTemplatesSidebar, {
  WidgetTemplatesSidebarRef,
} from './components/WidgetTemplatesSidebar/WidgetTemplatesSidebar';
import WidgetTemplateForm from './components/WidgetTemplateForm/WidgetTemplateForm';
import useWidgetTemplateActions from './hooks/useWidgetTemplateActions';
import useSelectedWidgetTemplate from './hooks/useSelectedWidgetTemplate';

/**
 * Container component for managing widget templates
 * Orchestrates sidebar (templates list) and main form (selected template)
 */
export default function WidgetTemplatesContainer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get breadcrumbs data
  const breadCrumbsData = getBreadCrumbsDataByKey({ pathname, t });

  // Check user access permissions
  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.HomePageManagement) ===
    AccessLevelEnum.Read;

  // Ref to access sidebar methods (refetch, updateTemplateInList)
  const sidebarRef = useRef<WidgetTemplatesSidebarRef>(null);

  // Error state from sidebar
  const [sidebarError, setSidebarError] = useState<ErrorLike | null>(null);

  // Initial loading state - shows loader until templates are first loaded
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Selected template state (own hook with own query)
  const {
    selectedTemplateId,
    selectedTemplate,
    loading: templateLoading,
    selectTemplate,
    selectTemplateById,
    clearSelection,
    updateSelectedTemplate,
  } = useSelectedWidgetTemplate();

  // Template actions (mutations)
  const { handleUpdate, handleDelete, updateLoading, deleteLoading } =
    useWidgetTemplateActions({
      refetchTemplates: () => sidebarRef.current?.refetch(),
      onDeleteSuccess: clearSelection,
      onUpdateSuccess: (updatedTemplate) => {
        // Update both sidebar list and selected template locally
        sidebarRef.current?.updateTemplateInList(updatedTemplate);
        updateSelectedTemplate(updatedTemplate);
      },
    });

  // Handle template selection from sidebar
  const handleSelectTemplate = useCallback(
    (template: WidgetTemplate) => {
      selectTemplate(template);
    },
    [selectTemplate],
  );

  // Navigate to create page
  const handleCreateClick = useCallback(() => {
    navigate(APP_URLS.dashboard.homeWidgetsCreate.path);
  }, [navigate]);

  // Handle sidebar error
  const handleSidebarError = useCallback((error: ErrorLike) => {
    setSidebarError(error);
  }, []);

  // Handle initial templates load - auto-select template based on URL param or first template
  const handleInitialLoad = useCallback(
    (templates: WidgetTemplate[]) => {
      setIsInitialLoading(false);

      // Check if there's a selectTemplateId param (e.g., after creating a new template)
      const selectTemplateIdParam = searchParams.get(
        GetUrlParams.SelectTemplateId,
      );

      if (selectTemplateIdParam) {
        // Clear the URL param after reading it
        searchParams.delete(GetUrlParams.SelectTemplateId);
        setSearchParams(searchParams, { replace: true });

        // Try to find the template in the loaded list
        const templateToSelect = templates.find(
          (template) => template.id === selectTemplateIdParam,
        );

        if (templateToSelect) {
          // Template found in list - select it directly
          selectTemplate(templateToSelect);
        } else {
          // Template not in list (e.g., sorted to different page) - fetch by ID
          selectTemplateById(selectTemplateIdParam);
        }
      } else if (templates.length > 0) {
        // Default behavior: select first template
        selectTemplate(templates[0]);
      }
    },
    [selectTemplate, selectTemplateById, searchParams, setSearchParams],
  );

  // Show error page if query failed
  if (sidebarError) {
    return (
      <ErrorPage
        status={getStatusFromError(sidebarError)}
        description={getDescriptionFromError(sidebarError)}
      />
    );
  }

  // Render page body content based on state
  const renderPageBodyContent = () => {
    // Show loader during initial load
    if (isInitialLoading) {
      return <CircleLoader />;
    }

    // Show loader while fetching selected template data
    if (selectedTemplateId && templateLoading) {
      return <CircleLoader />;
    }

    if (selectedTemplate) {
      return (
        <WidgetTemplateForm
          key={selectedTemplate.id}
          template={selectedTemplate}
          deleteLoading={deleteLoading}
          loading={updateLoading || deleteLoading}
          onSave={handleUpdate}
          onDelete={handleDelete}
          onCancel={clearSelection}
          isViewOnly={isViewOnly}
        />
      );
    }

    return (
      <EmptyState
        title={t('home.no-widget-selected')}
        text={t('home.no-widget-selected-description')}
        icon={IconName.Home}
      />
    );
  };

  // Sidebar component with ref for parent access
  const sidebar = (
    <WidgetTemplatesSidebar
      ref={sidebarRef}
      selectedTemplateId={selectedTemplateId}
      onSelectTemplate={handleSelectTemplate}
      onError={handleSidebarError}
      onInitialLoad={handleInitialLoad}
    />
  );

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <DashboardContentWithRightSidebar sidebar={sidebar} toggleBtnTop={62.5}>
        <PageHeader title={t('home.manage-widget-templates')}>
          {!isViewOnly ? (
            <Button
              buttonText={t('create-new')}
              buttonType={ButtonTypes.primaryFilled}
              leftIcon="add"
              onClick={handleCreateClick}
            />
          ) : undefined}
        </PageHeader>
        <PageBody>{renderPageBodyContent()}</PageBody>
      </DashboardContentWithRightSidebar>
    </>
  );
}
